/* eslint-disable no-mixed-spaces-and-tabs */
/* eslint-disable max-statements */
import axios from 'axios';
import React, { useEffect, useRef, useState } from 'react';
import Scrollbars from 'react-custom-scrollbars-2';
import {
	QueryClient,
	QueryClientProvider,
	useMutation,
	useQuery
} from 'react-query';
import { useNavigate, useParams } from 'react-router-dom';
import Editable from '../../Utils/editable';
import './dashCard.css';
import moment from 'moment';
import { stateNotificationsEnum } from '../../Utils/notification';
import {
	setSelectedSection,
	setTab
} from '../../../redux/actions/sideBarAction';
import { useDispatch } from 'react-redux';
import { useTypedSelector } from '../../../hooks/useTypeSelector';
import { SetNotificationAction } from '../../../redux/actions/notificationAction';
import { globalEnv } from '../../../config/baseConfig';
import { DataNotFoundAnimation } from '../../Utils/ErrorAnimations/dataNotFoundAnim';
import LoadingAnimComponent from '../../Utils/LoadingAnimations/loadingAnimation';
import { hideSearchbarAction } from '../../../redux/actions/searchBarAction';
import axiosInstance from '../../../config/axiosInstance';
import LazyImage from '../../Utils/Components/lazyImageComponent';
import validator from 'validator';
import { DatePickerComponent } from '../../Utils/Components/datePicker/datePicker';

const queryClient = new QueryClient();

export default function EditUser() {
	const { user }: any = useTypedSelector((state) => state.auth);
	const { id } = useParams();
	const [isProfile] = useState(user._id === id);
	const dispatch = useDispatch();
	dispatch(setSelectedSection('User Details'));
	dispatch(hideSearchbarAction());

	const { ACCESS_TOKEN } = useTypedSelector((state) => state.auth);

	useEffect(() => {
		if (isProfile) dispatch(setTab('profile'));
		else dispatch(setTab('documents'));
	}, [isProfile]);
	return (
		<QueryClientProvider client={queryClient}>
			<div className="pt-16 h-full w-full">
				<EditUserComponent id={id!} ACCESS_TOKEN={ACCESS_TOKEN} />
			</div>
		</QueryClientProvider>
	);
}

type UserDetailsProps = {
	id: string;
	ACCESS_TOKEN: string;
};

function EditUserComponent({ id, ACCESS_TOKEN }: UserDetailsProps) {
	const [isChanged, setIsChanged] = useState(false);

	const [userId, setUserId] = useState('');
	const [username, setUsername] = useState('');
	const [bio, setBio] = useState('');
	const [email, setEmail] = useState('');
	const [photo, setPhoto] = useState(null);
	const [gender, setGender] = useState('male');
	const [birthdate, setBirthdate] = useState<Date | null>(new Date());
	const [location, setLocation] =
		useState<{ city?: string; country?: string }>();

	type SocialMediaType = {
		type: string;
		link: string;
	};

	const [instagramLink, setInstagramLink] = useState('');
	const [facebookLink, setFacebookLink] = useState('');
	const [youtubeLink, setYoutubeLink] = useState('');
	const [twitterLink, setTwitterLink] = useState('');
	const [pinterestLink, setPinterestLink] = useState('');

	const usernameRef = useRef<null>(null);
	const emailRef = useRef<null>(null);

	const [isSaving, setIsSaving] = useState(false);

	const [usernameErrorMsg, setUsernameErrorMsg] = useState({
		message: '',
		error: false
	});

	// handleResize() & dimensions are added to update the dashboard card text size when resizing the window
	const [dimensions, setDimensions] = useState({
		width: window.innerWidth,
		height: window.innerHeight
	});
	useEffect(() => {
		function handleResize() {
			setDimensions({
				width: window.innerWidth,
				height: window.innerHeight
			});
		}
		window.addEventListener('resize', handleResize);
		return () => {
			window.removeEventListener('resize', handleResize);
		};
	}, []);

	async function fetchUserData(id: string, token: string) {
		const config = {
			headers: {
				Authorization: 'Bearer ' + token
			}
		};
		const { data } = await axiosInstance().get(
			`${globalEnv.BASE_URL}/editor/${id}/get-details`,
			config
		);
		return data.user;
	}

	const { data, isLoading, isError } = useQuery(
		['users', id],
		() => fetchUserData(id!, ACCESS_TOKEN),
		{
			keepPreviousData: true,
			refetchOnWindowFocus: false,
			staleTime: 5000
		}
	);

	const navigate = useNavigate();

	useEffect(() => {
		if (data != null) {
			setUserId(data._id);
			setUsername(data.username);
			setEmail(data.email);
			setBio(data.bio);
			setGender(data.gender);
			setLocation(data.location);
			setBirthdate(moment(data.birthdate).toDate());
			if (data.socialMediaLinks != null) {
				setInstagramLink(
					() =>
						data.socialMediaLinks.find(
							(item: SocialMediaType) => item.type === 'instagram'
						).link
				);
				setFacebookLink(
					() =>
						data.socialMediaLinks.find(
							(item: SocialMediaType) => item.type === 'facebook'
						).link
				);
				setYoutubeLink(
					() =>
						data.socialMediaLinks.find(
							(item: SocialMediaType) => item.type === 'youtube'
						).link
				);
				setTwitterLink(
					() =>
						data.socialMediaLinks.find(
							(item: SocialMediaType) => item.type === 'twitter'
						).link
				);
				setPinterestLink(
					() =>
						data.socialMediaLinks.find(
							(item: SocialMediaType) => item.type === 'pinterest'
						).link
				);
			}
			if (data.photo != null) setPhoto(data.photo);
		}
	}, [data]);

	const dispatch = useDispatch();

	const saveUserReq = async () => {
		const dto = {
			userId: userId,
			username: username,
			email: email,

			birthdate: birthdate,
			gender: gender,
			bio: bio,
			location: location,
			socialMediaLinks: [
				{ type: 'instagram', link: instagramLink },
				{ type: 'facebook', link: facebookLink },
				{ type: 'youtube', link: youtubeLink },
				{ type: 'pinterest', link: pinterestLink },
				{ type: 'twitter', link: twitterLink }
			]
		};

		const config = {
			headers: {
				Authorization: 'Bearer ' + ACCESS_TOKEN
			}
		};
		const { data: response } = await axios.put(
			globalEnv.BASE_URL + '/user/current-user',
			dto,
			config
		);
		return response;
	};

	const deleteUserReq = async () => {
		const { data: response } = await axios.delete(
			globalEnv.BASE_URL + '/user/current-user',
			{
				data: {
					userId: userId
				},
				headers: {
					Authorization: 'Bearer ' + ACCESS_TOKEN
				}
			}
		);
		return response;
	};

	const saveUser = useMutation(saveUserReq, {
		onMutate: () => setIsSaving(true),
		onSuccess: () => {
			setIsSaving(false);
			setIsChanged(false);
			dispatch(
				SetNotificationAction({
					type: stateNotificationsEnum.SUCCESS,
					message: 'Successfully updated user info.'
				})
			);
		},
		onError: () => {
			setIsSaving(false);
			dispatch(
				SetNotificationAction({
					type: stateNotificationsEnum.ERROR,
					message: "Couldn't update user info."
				})
			);
		}
	});

	const deleteUser = useMutation(deleteUserReq, {
		onSuccess: () => {
			dispatch(
				SetNotificationAction({
					type: stateNotificationsEnum.SUCCESS,
					message: 'User deleted.'
				})
			);
			navigate('../documents');
		},

		onError: () => {
			dispatch(
				SetNotificationAction({
					type: stateNotificationsEnum.ERROR,
					message: "Couldn't delete user."
				})
			);
		}
	});

	const verifField = (name = '') => {
		if (validator.isEmpty(name, { ignore_whitespace: true })) {
			setUsernameErrorMsg({
				error: true,
				message: 'Please enter your email address'
			});
		}
	};

	return (
		<Scrollbars autoHide>
			{isLoading ? (
				<LoadingAnimComponent />
			) : isError ? (
				<div className="h-full ml-18 ">
					<div className=" flex flex-col mx-auto my-auto text-center align-middle overflow-clip h-full ">
						<div className="mx-auto my-auto self-center">
							<div className="min-h-16">
								<DataNotFoundAnimation height={dimensions.height / 1.5} />
							</div>
							<h3 className="lg:text-2xl text-xl font-bold dark:text-gray-200 text-gray-700 -mt-8">
								{"Couldn't find the data you're looking for!"}
							</h3>
						</div>
					</div>
				</div>
			) : (
				<main className="ml-16">
					<section className="relative py-16 transition-all duration-500 bg-gray-300 dark:bg-gray-900">
						<div
							className="relative w-11/12 flex flex-col min-w-0 break-words bg-white dark:bg-gray-800 
							transition-all duration-500 mb-6 shadow-xl rounded-lg container mx-auto px-4"
						>
							<div className="flex flex-wrap justify-center">
								<div className="w-full lg:w-3/12 px-4 lg:order-2 flex justify-center">
									<label
										htmlFor="myInput"
										className="mask mask-hexagon-2 flex rounded-full justify-center bg-gray-300 dark:bg-gray-700 border-none absolute -m-16"
									>
										<LazyImage
											photo={
												photo != null
													? photo
													: `https://api.dicebear.com/7.x/bottts/svg?seed=${username}`
											}
											dimesnions={{ width: '140', height: '140' }}
											loaderDimesnions={80}
										/>
									</label>
									<input
										id="myInput"
										style={{ display: 'none' }}
										type={'file'}
										// onChange={this.fileSelectedHandler}
									/>
								</div>
							</div>
							<div className="text-center mt-20">
								<div className="relative group max-w-md mx-auto">
									<Editable
										text={username}
										placeholder="Username"
										childRef={usernameRef}
										type="input"
										className="text-4xl font-semibold leading-normal"
										textColor="dark:text-primary-text-dark text-primary-text-light transition-colors duration-500"
									>
										<input
											ref={usernameRef}
											type="text"
											name="name"
											className="shadow rounded text-4xl font-semibold leading-normal text-primary-text-light text-center 
													dark:bg-base-bg-dark transition-all duration-500 dark:text-primary-text-dark"
											placeholder=""
											value={username}
											onChange={(e) => {
												setUsername(e.target.value);
												setIsChanged(true);
											}}
										/>
									</Editable>

									<span
										className="absolute rounded-md shadow-md text-white bg-gray-900 p-2 min-w-max text-xs
										  scale-0 origin-top group-hover:scale-100 z-50 font-bold transition-all 
										  duration-500 opacity-0 group-hover:opacity-100 -top-6 right-32"
									>
										{'Click to change your username'}
									</span>
								</div>

								<div className="text-sm leading-normal font-bold relative group max-w-md mx-auto">
									<Editable
										text={email}
										placeholder="Email"
										childRef={emailRef}
										type="input"
										className="font-semibold leading-normal transition-colors duration-500"
										textColor="text-gray-600 dark:text-gray-100 transition-all duration-500"
									>
										<input
											ref={emailRef}
											type="text"
											name="name"
											className="shadow rounded font-semibold leading-normal text-gray-600 text-center dark:bg-gray-800 
														dark:text-gray-100 transition-all duration-500"
											placeholder=""
											value={email}
											onChange={(e) => {
												setEmail(e.target.value);
												setIsChanged(true);
											}}
										/>
									</Editable>

									<span
										className="absolute rounded-md shadow-md text-white bg-gray-900 p-2 min-w-max text-xs
										  scale-0 origin-top group-hover:scale-100 z-50 font-bold transition-all 
										  duration-500 opacity-0 group-hover:opacity-100 top-7 right-32"
									>
										{'Click to change your email'}
									</span>
								</div>

								<div className="px-64 mt-5 mb-5">
									<h3 className="font-bold mb-3 text-left dark:text-white">
										Optional fields
									</h3>
									<form className="mx-auto w-full max-w-3xl">
										<div className="flex flex-wrap -mx-3 mb-6">
											<div className="w-full px-3 text-left">
												<label
													className="block uppercase tracking-wide text-gray-700 
														dark:text-gray-300 text-xs font-bold mb-2"
												>
													Bio
												</label>
												<textarea
													className="inputStyle"
													id="grid-title"
													placeholder="Write something about yourself"
													value={bio}
													onChange={(e) => {
														setIsChanged(true);
														setBio(e.target.value);
													}}
												/>
											</div>
										</div>
										<div className="flex flex-wrap -mx-3 mb-6">
											<div className="w-full px-3 text-left">
												<label
													className="block uppercase tracking-wide text-gray-700 
														dark:text-gray-300 text-xs font-bold mb-2"
												>
													City
												</label>
												<input
													className="inputStyle"
													id="grid-title"
													placeholder="Please specify your city"
													value={location?.city}
													onChange={(e) => {
														setIsChanged(true);
														setLocation({
															...location,
															city: e.target.value
														});
													}}
												/>
											</div>
										</div>
										<div className="flex flex-wrap -mx-3 mb-6">
											<div className="w-full px-3 text-left">
												<label
													className="block uppercase tracking-wide text-gray-700 
														dark:text-gray-300 text-xs font-bold mb-2"
												>
													Country
												</label>
												<input
													className="inputStyle"
													id="grid-title"
													placeholder="Please specify your country"
													value={location?.country}
													onChange={(e) => {
														setIsChanged(true);
														setLocation({
															...location,
															country: e.target.value
														});
													}}
												/>
											</div>
										</div>

										<div className="w-40 mb-8">
											<label
												className="block uppercase tracking-wide text-gray-700 
														dark:text-gray-300 text-xs font-bold mb-2 text-left"
											>
												{'Gender'}
											</label>
											<select
												id={'Gender'}
												className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
												onChange={(e) => {
													setIsChanged(true);
													setGender(e.target.value);
												}}
											>
												<option selected={gender === 'male'} value={'male'}>
													Male
												</option>
												<option selected={gender === 'female'} value={'female'}>
													Female
												</option>
											</select>
										</div>

										<div className="flex flex-wrap -mx-3 mb-6">
											<div className="w-full px-3 text-left">
												<label
													className="block uppercase tracking-wide text-gray-700 
														dark:text-gray-300 text-xs font-bold mb-2"
												>
													Date of birth
												</label>

												<DatePickerComponent
													date={birthdate}
													setDate={setBirthdate}
													setIsChanged={setIsChanged}
												/>
											</div>
										</div>
									</form>
								</div>

								<div className="px-64 mt-8 mb-5">
									<h3 className="font-bold mb-3 text-left dark:text-white">
										Social Media links
									</h3>
									<form className="mx-auto w-full max-w-3xl">
										<div className="flex flex-wrap -mx-3 mb-6">
											<div className="w-full px-3 text-left">
												<label
													className="block uppercase tracking-wide text-gray-700 
														dark:text-gray-300 text-xs font-bold mb-2"
												>
													Instagram
												</label>
												<input
													className="inputStyle"
													id="grid-title"
													placeholder="Instagram account link"
													value={instagramLink}
													onChange={(e) => {
														setIsChanged(true);
														setInstagramLink(e.target.value);
													}}
												/>
											</div>
										</div>

										<div className="flex flex-wrap -mx-3 mb-6">
											<div className="w-full px-3 text-left">
												<label
													className="block uppercase tracking-wide text-gray-700 
														dark:text-gray-300 text-xs font-bold mb-2"
												>
													Facebook
												</label>
												<input
													className="inputStyle"
													id="grid-title"
													placeholder="Facebook account link"
													value={facebookLink}
													onChange={(e) => {
														setIsChanged(true);
														setFacebookLink(e.target.value);
													}}
												/>
											</div>
										</div>

										<div className="flex flex-wrap -mx-3 mb-6">
											<div className="w-full px-3 text-left">
												<label
													className="block uppercase tracking-wide text-gray-700 
														dark:text-gray-300 text-xs font-bold mb-2"
												>
													Twitter
												</label>
												<input
													className="inputStyle"
													id="grid-title"
													placeholder="Twitter account link"
													value={twitterLink}
													onChange={(e) => {
														setIsChanged(true);
														setTwitterLink(e.target.value);
													}}
												/>
											</div>
										</div>

										<div className="flex flex-wrap -mx-3 mb-6">
											<div className="w-full px-3 text-left">
												<label
													className="block uppercase tracking-wide text-gray-700 
														dark:text-gray-300 text-xs font-bold mb-2"
												>
													Youtube
												</label>
												<input
													className="inputStyle"
													id="grid-title"
													placeholder="Youtube channel link"
													value={youtubeLink}
													onChange={(e) => {
														setIsChanged(true);
														setYoutubeLink(e.target.value);
													}}
												/>
											</div>
										</div>

										<div className="flex flex-wrap -mx-3 mb-6">
											<div className="w-full px-3 text-left">
												<label
													className="block uppercase tracking-wide text-gray-700 
														dark:text-gray-300 text-xs font-bold mb-2"
												>
													Pinterest
												</label>
												<input
													className="inputStyle"
													id="grid-title"
													placeholder="Pinterest account link"
													value={pinterestLink}
													onChange={(e) => {
														setIsChanged(true);
														setPinterestLink(e.target.value);
													}}
												/>
											</div>
										</div>
									</form>
								</div>
							</div>

							<div className="w-full px-4">
								<div className="py-6 px-3 flex flex-row">
									<div className="justify-start flex-1">
										<button
											className="btn btn-outline btn-sm btn-error mr-1"
											type="button"
											style={{ transition: 'all .15s ease' }}
											onClick={() => deleteUser.mutate()}
										>
											Delete your account
										</button>
									</div>
									<div className="justify-end">
										<button
											className="btn btn-outline btn-sm mt-2 mr-1 "
											type="button"
											style={{ transition: 'all .15s ease' }}
											onClick={() => navigate('../documents')}
										>
											Cancel
										</button>
										{isChanged && (
											<button
												disabled={isLoading ? true : false}
												type="button"
												className="btn btn-outline btn-sm btn-primary mt-2 ml-1"
												onClick={() => saveUser.mutate()}
											>
												save
												{isSaving ? (
													<div className="pl-2">
														<svg
															role="status"
															className="w-5 h-5 text-gray-200 animate-spin dark:text-gray-600 fill-blue-600"
															viewBox="0 0 100 101"
															fill="none"
															xmlns="http://www.w3.org/2000/svg"
														>
															<path
																d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
																fill="currentColor"
															></path>
															<path
																d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
																fill="currentFill"
															></path>
														</svg>
													</div>
												) : (
													<h5 className="text-xs ml-2">💾</h5>
												)}
											</button>
										)}
									</div>
								</div>
							</div>
						</div>
					</section>
				</main>
			)}
		</Scrollbars>
	);
}
